import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import { DataGrid, GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import SearchIcon from "@mui/icons-material/Search";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import * as XLSX from "xlsx";

import { useAuth } from "../../hooks/useAuth";
import { getCompanys } from "../../api/companysService";
import { ContactPayload, createContact, deleteContact, getContacts, importContactsBulk, updateContact } from "../../api/contactsService";
import PageCard from "../../components/ui/PageCard";
import MagazinePageHeader from "../magazine/MagazinePageHeader";
import MagazineNewButton from "../magazine/MagazineNewButton";
import { ActionContainer } from "../companys/style";

type ContactForm = {
  company_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  is_primary: boolean;
  notes: string;
  remind_at: string;
};

const emptyForm: ContactForm = {
  company_id: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  job_title: "",
  is_primary: false,
  notes: "",
  remind_at: "",
};

const useQuery = () => new URLSearchParams(useLocation().search);

const Contacts = () => {
  const { canEditContacts, isAdmin } = useAuth();
  const query = useQuery();
  const defaultCompanyId = query.get("company_id") || "";

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ContactForm>({ ...emptyForm, company_id: defaultCompanyId });
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null);

  const load = async (companyId?: string) => {
    setLoading(true);
    const [contactsData, companyData] = await Promise.all([
      getContacts({ company_id: companyId || undefined }),
      getCompanys(""),
    ]);
    setItems(contactsData);
    setCompanies(companyData);
    setLoading(false);
  };

  useEffect(() => {
    load(defaultCompanyId || undefined);
  }, []);

  const companyOptions = useMemo(() => companies.sort((a: any, b: any) => a.name.localeCompare(b.name)), [companies]);

  const closeDialog = () => {
    setOpen(false);
    setEditingId(null);
    setForm({ ...emptyForm, company_id: defaultCompanyId });
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, company_id: defaultCompanyId });
    setOpen(true);
  };

  const submit = async () => {
    if (!canEditContacts) return;
    if (!form.company_id || !form.first_name) return;

    const payload: any = {
      company_id: Number(form.company_id),
      first_name: form.first_name,
      last_name: form.last_name || null,
      email: form.email || null,
      phone: form.phone || null,
      job_title: form.job_title || null,
      is_primary: form.is_primary,
      notes: form.notes || null,
      remind_at: form.remind_at ? new Date(form.remind_at).toISOString() : null,
    };

    if (editingId) {
      await updateContact(editingId, payload);
    } else {
      await createContact(payload);
    }

    closeDialog();
    load(defaultCompanyId || undefined);
  };

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((row) => [row.first_name, row.last_name, row.email, row.phone, row.job_title, row.company?.name].some((value) => String(value || "").toLowerCase().includes(term)));
  }, [items, search]);

  const workbookDownload = (rows: any[], filename: string) => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows), "Contacts");
    XLSX.writeFile(workbook, filename);
  };

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new();
    const headers = [["Company ID", "Company Name", "First Name", "Last Name", "Email", "Phone", "Job Title", "Primary Contact"]];
    const contactsSheet = XLSX.utils.aoa_to_sheet(headers);
    contactsSheet["!cols"] = [{ wch: 12 }, { wch: 30 }, { wch: 18 }, { wch: 18 }, { wch: 32 }, { wch: 18 }, { wch: 24 }, { wch: 16 }];
    const instructionsSheet = XLSX.utils.aoa_to_sheet([
      ["Contacts import instructions"],
      ["1", "Do not rename, remove, or reorder columns on the Contacts sheet."],
      ["2", "Company ID and First Name are required. Company Name is included for easy reference."],
      ["3", "Copy the Company ID and Company Name from the Companies sheet."],
      ["4", "Primary Contact accepts Yes/No, True/False, or 1/0."],
      ["5", "Keep phone numbers as text to preserve leading zeroes."],
      ["6", "One row creates one contact. Remove empty rows before importing."],
    ]);
    instructionsSheet["!cols"] = [{ wch: 8 }, { wch: 90 }];
    const companiesSheet = XLSX.utils.json_to_sheet(companyOptions.map((company: any) => ({ "Company ID": company.id, "Company Name": company.name })));
    companiesSheet["!cols"] = [{ wch: 12 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(workbook, contactsSheet, "Contacts");
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");
    XLSX.utils.book_append_sheet(workbook, companiesSheet, "Companies");
    XLSX.writeFile(workbook, "contacts-import-template.xlsx");
  };
  const exportContacts = () => workbookDownload(items.map((row) => ({ Company: row.company?.name || "", "First name": row.first_name || "", "Last name": row.last_name || "", Email: row.email || "", Phone: row.phone || "", "Job title": row.job_title || "", Primary: row.is_primary ? "Yes" : "No", Notes: row.notes || "", Reminder: row.remind_at || "" })), "contacts-export.xlsx");

  const importContacts = async (file?: File) => {
    if (!file) return;
    setImporting(true);
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const contactsSheet = workbook.Sheets.Contacts || workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(contactsSheet, { defval: "" });
      const errors: string[] = [];
      const contactsToImport: ContactPayload[] = [];
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const companyId = Number(row["Company ID"]);
        const companyName = String(row["Company Name"] || row.Company || "").trim();
        const company = companies.find((c) => (companyId && Number(c.id) === companyId) || (!companyId && c.name.trim().toLowerCase() === companyName.toLowerCase()));
        const firstName = String(row["First Name"] || row["First name"] || "").trim();
        if (!company) { errors.push(`Row ${index + 2}: company was not found.`); continue; }
        if (!firstName) { errors.push(`Row ${index + 2}: First Name is required.`); continue; }
        contactsToImport.push({ company_id: company.id, first_name: firstName, last_name: row["Last Name"] || row["Last name"] || null, email: row.Email || null, phone: row.Phone ? String(row.Phone) : null, job_title: row["Job Title"] || row["Job title"] || null, is_primary: /^(yes|true|1)$/i.test(String(row["Primary Contact"] || row.Primary)) });
      }
      const result = contactsToImport.length ? await importContactsBulk(contactsToImport) : { imported: 0 };
      const imported = result ? Number(result.imported ?? result.count ?? result.created ?? contactsToImport.length) : 0;
      if (!result && contactsToImport.length) errors.push("The bulk import request failed. No validated contacts were imported.");
      if (Array.isArray(result?.errors)) errors.push(...result.errors.map((error: any) => typeof error === "string" ? error : error.message || JSON.stringify(error)));
      await load(defaultCompanyId || undefined);
      setImportResult({ imported, skipped: Math.max(0, rows.length - imported), errors });
    } finally { setImporting(false); }
  };

  const onDelete = async (id: number) => {
    if (!canEditContacts) return;
    if (!window.confirm("Delete this contact?")) return;
    await deleteContact(id);
    load(defaultCompanyId || undefined);
  };

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: "name",
        headerName: "Name",
        width: 220,
        valueGetter: (params: GridValueGetterParams) =>
          [params.row.first_name, params.row.last_name].filter(Boolean).join(" "),
      },
      { field: "company", headerName: "Company", width: 240, valueGetter: (p) => p.row.company?.name || "—" },
      { field: "email", headerName: "Email", width: 220 },
      { field: "phone", headerName: "Phone", width: 150 },
      { field: "job_title", headerName: "Title", width: 180, valueGetter: (p) => p.row.job_title || "—" },
      { field: "is_primary", headerName: "Primary", width: 100, valueGetter: (p) => (p.row.is_primary ? "Yes" : "") },
    ];

    if (canEditContacts) {
      cols.push({
        field: "action",
        headerName: "Action",
        width: 180,
        renderCell: (params: GridValueGetterParams) => (
          <ActionContainer>
            <Link className="btn viewBtn no-underline" to={`/crm/contacts/${params.row.id}`}>View</Link>
            <button type="button" className="btn deleteBtn" onClick={() => onDelete(params.row.id)}>Delete</button>
          </ActionContainer>
        ),
      });
    }

    return cols;
  }, [canEditContacts, companies]);

  return (
    <Layout>
      <MagazinePageHeader
        title="Contacts"
        description="Manage contacts linked to companies."
        action={<div className="flex flex-wrap gap-2"><button type="button" onClick={() => setImportOpen(true)} className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><FileUploadOutlinedIcon fontSize="small" />Import contacts</button>{isAdmin && <button type="button" onClick={exportContacts} className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><FileDownloadOutlinedIcon fontSize="small" />Export contacts</button>}{canEditContacts && <MagazineNewButton onClick={openNew} label="New contact" />}</div>}
      />

      <PageCard>
        <div className="mb-4 flex max-w-xl items-center rounded-md border border-gray-300 bg-white px-3 focus-within:border-[#5949d1] focus-within:ring-1 focus-within:ring-[#5949d1]"><SearchIcon className="text-gray-400" fontSize="small" /><input value={search} onChange={(e) => setSearch(e.target.value)} className="w-full border-0 px-3 py-2.5 text-sm outline-none" placeholder="Search by name, company, email, phone or title" /></div>
        <DataGrid
          rows={filteredItems}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          autoHeight
          loading={loading}
          sx={{ border: "none" }}
        />
      </PageCard>

      <Dialog open={importOpen} onClose={() => !importing && setImportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Import contacts</DialogTitle>
        <DialogContent dividers>
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
            <h3 className="font-semibold text-blue-950">Prepare your spreadsheet</h3>
            <p className="mt-1 text-sm text-blue-800">Use the template so every contact is added to the correct field and company.</p>
            <button type="button" onClick={downloadTemplate} className="mt-3 inline-flex items-center gap-2 rounded-md border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-50"><FileDownloadOutlinedIcon fontSize="small" />Download Excel template</button>
          </div>
          <div className="mt-5">
            <p className="text-sm font-medium text-gray-800">Template columns</p>
            <p className="mt-1 text-sm text-gray-500">Company ID, Company Name, First Name, Last Name, Email, Phone, Job Title, Primary Contact</p>
            <p className="mt-2 text-xs text-amber-700">Use the Companies worksheet to copy the correct company ID. This prevents contacts being assigned to similarly named companies.</p>
          </div>
          <label className="mt-5 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center hover:border-[#5949d1] hover:bg-violet-50">
            <FileUploadOutlinedIcon className="text-[#5949d1]" />
            <span className="mt-2 text-sm font-medium text-gray-800">{importFile ? importFile.name : "Choose an Excel or CSV file"}</span>
            <span className="mt-1 text-xs text-gray-500">.xlsx, .xls or .csv</span>
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" disabled={importing} onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} />
          </label>
          {importResult && <div className={`mt-4 rounded-lg border p-4 ${importResult.errors.length ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`}><p className="font-medium text-gray-900">Imported {importResult.imported} contact{importResult.imported === 1 ? "" : "s"}. Skipped {importResult.skipped}.</p>{importResult.errors.length > 0 && <ul className="mt-2 max-h-32 list-disc overflow-y-auto pl-5 text-sm text-amber-800">{importResult.errors.map((error) => <li key={error}>{error}</li>)}</ul>}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportOpen(false); setImportFile(null); setImportResult(null); }} disabled={importing}>{importResult ? "Close" : "Cancel"}</Button>
          <Button variant="contained" onClick={() => importContacts(importFile || undefined)} disabled={!importFile || importing}>{importing ? "Importing..." : "Import contacts"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Edit contact" : "New contact"}</DialogTitle>
        <DialogContent dividers>
          <div className="grid gap-x-5 sm:grid-cols-2"><TextField
            select
            fullWidth
            label="Company"
            value={form.company_id}
            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            margin="normal"
            required
          >
            <MenuItem value="">Select company</MenuItem>
            {companyOptions.map((c: any) => (
              <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="First name"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Last name"
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            margin="normal"
          />
          </div>
          <TextField
            fullWidth
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Job title"
            value={form.job_title}
            onChange={(e) => setForm({ ...form, job_title: e.target.value })}
            margin="normal"
          />
          <FormControlLabel
            control={<Checkbox checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} />}
            label="Primary contact"
          />
          <TextField
            fullWidth
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            margin="normal"
            multiline
            minRows={3}
          />
          <TextField fullWidth type="datetime-local" label="Next reminder" value={form.remind_at} onChange={(e) => setForm({ ...form, remind_at: e.target.value })} margin="normal" InputLabelProps={{ shrink: true }} helperText="Schedule the next call or follow-up." />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={submit} variant="contained" disabled={!canEditContacts}>
            {editingId ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Contacts;

